import type { CSSProperties } from "react"
import { useEffect, useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import { QRCode } from "react-qr-code"
import { ActionButton } from "../../components/ActionButton"
import { Badge } from "../../components/Badge"
import { GalleryLightbox } from "../../components/GalleryLightbox"
import { IconBadge } from "../../components/IconBadge"
import { SocialIconBadge } from "../../components/SocialIconBadge"
import { TextInput } from "../../components/TextInput"
import { contrastingTextColor, hexToRgb, normalizeHexColor } from "../../utils/color"
import { dayNames } from "../../utils/hebrew"
import { getSocialPlatformColor, getSocialPlatformLabel } from "../../utils/socialPlatforms"
import { getCardTemplate } from "../../utils/cardTemplates"

type CardData = {
  slug: string
  title: string
  subtitle: string | null
  description: string | null
  phone: string | null
  whatsapp: string | null
  whatsapp_message: string | null
  whatsapp_url: string | null
  email: string | null
  website_url: string | null
  address: string | null
  maps_url: string | null
  is_published: boolean
  theme: string
  primary_color: string
  logo_focus_x: number
  logo_focus_y: number
  logo_radius: number
  cover_focus_x: number
  cover_focus_y: number
  show_contact_button: boolean
  show_vcf_button: boolean
  show_feedback_button: boolean
  opening_hours_hidden: boolean
  content_sections_order: string[]
  hidden_sections: string[]
  section_titles: Record<string, string>
  hidden_section_titles: string[]
  categories: string[]
  vcf_url: string
  public_url: string
  logo_url: string | null
  cover_image_url: string | null
  gallery_images: Array<{ id: number; url: string; category: string; description: string | null }>
  social_links: Array<{ id: number; platform: string; handle: string | null; url: string; icon_color: string | null }>
  opening_hours: Array<{ day_of_week: number; opens_at: string | null; closes_at: string | null; is_closed: boolean }>
  buttons: Array<{ id: number; label: string; url: string; icon_name: string | null; button_color: string | null }>
  faqs: Array<{ id: number; question: string; answer: string; position: number }>
  testimonials: Array<{ id: number; name: string; role: string | null; message: string; rating: number }>
}

type Props = {
  card: CardData
  preview?: boolean
}

function structuredDataFor(card: CardData) {
  const images = [card.logo_url, card.cover_image_url, ...card.gallery_images.map((image) => image.url)].filter(Boolean)
  const sameAs = card.social_links.map((link) => link.url).filter(Boolean)
  const openingHoursSpecification = card.opening_hours
    .filter((hour) => !hour.is_closed && hour.opens_at && hour.closes_at)
    .map((hour) => ({
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][hour.day_of_week],
      opens: hour.opens_at,
      closes: hour.closes_at,
    }))
  const businessType = card.address || card.phone ? "LocalBusiness" : "Organization"
  const entity = {
    "@type": businessType,
    name: card.title,
    description: card.description || card.subtitle || card.title,
    url: card.public_url,
    ...(images.length > 0 ? { image: images } : {}),
    ...(card.phone ? { telephone: card.phone } : {}),
    ...(card.email ? { email: card.email } : {}),
    ...(card.website_url ? { sameAs: [...sameAs, card.website_url] } : sameAs.length > 0 ? { sameAs } : {}),
    ...(card.address
      ? {
          address: {
            "@type": "PostalAddress",
            streetAddress: card.address,
          },
        }
      : {}),
    ...(openingHoursSpecification.length > 0 ? { openingHoursSpecification } : {}),
  }

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    name: `${card.title} | Cardly`,
    url: card.public_url,
    mainEntity: entity,
  }
}

export default function CardShow({ card, preview }: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [galleryCategory, setGalleryCategory] = useState("all")
  const [faqOpenIndex, setFaqOpenIndex] = useState<number | null>(null)
  const [testimonialIndex, setTestimonialIndex] = useState(0)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false)
  const {
    data: contactData,
    setData: setContactData,
    post: postContact,
    processing: contactProcessing,
    errors: contactErrors,
    reset: resetContact,
  } = useForm({
    name: "",
    phone: "",
    message: "",
  })
  const {
    data: feedbackData,
    setData: setFeedbackData,
    post: postFeedback,
    processing: feedbackProcessing,
    errors: feedbackErrors,
    reset: resetFeedback,
  } = useForm({
    name: "",
    role: "",
    rating: "5",
    message: "",
  })
  const shareText = `${card.title} - ${card.public_url}`
  const trackAction = (action: string) => {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    void fetch(`/${card.slug}/action`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { "X-CSRF-Token": token } : {}) },
      credentials: "same-origin",
      body: JSON.stringify({ action_name: action }),
    }).catch(() => undefined)
  }
  const primary = normalizeHexColor(card.primary_color)
  const primaryText = contrastingTextColor(primary)
  const { r, g, b } = hexToRgb(primary)
  const sectionOrder = card.content_sections_order.length > 0 ? card.content_sections_order : ["hero", "info", "buttons", "social_links", "opening_hours", "faq", "testimonials", "gallery"]
  const sectionVisible = (section: string) => !card.hidden_sections.includes(section)
  const sectionTitleVisible = (section: string) => !card.hidden_section_titles.includes(section)
  const sectionTitle = (section: string) =>
    card.section_titles[section] || {
      hero: "כותרת עליונה",
      info: "פרטי קשר",
      buttons: "כפתורים",
      social_links: "קישורים חברתיים",
      opening_hours: "שעות פתיחה",
      faq: "שאלות נפוצות",
      testimonials: "המלצות",
      gallery: "גלריה",
    }[section] || section
  const pageStyle = {
    "--card-primary": primary,
    "--card-primary-rgb": `${r} ${g} ${b}`,
    "--card-primary-text": primaryText,
  } as CSSProperties
  const template = getCardTemplate(card.theme)
  const pageTitle = `${card.title} | Cardly`
  const pageDescription = card.description || card.subtitle || card.title
  const logoPosition = `${card.logo_focus_x ?? 50}% ${card.logo_focus_y ?? 50}%`
  const coverPosition = `${card.cover_focus_x ?? 50}% ${card.cover_focus_y ?? 50}%`
  const galleryCategories = Array.from(new Set(card.gallery_images.map((image) => image.category).filter(Boolean)))
  const visibleGalleryImages = galleryCategory === "all"
    ? card.gallery_images
    : card.gallery_images.filter((image) => image.category === galleryCategory)
  const socialImage = card.cover_image_url || card.logo_url
  const structuredData = JSON.stringify(structuredDataFor(card)).replace(/</g, "\\u003c")

  useEffect(() => {
    if (!contactModalOpen && !feedbackModalOpen) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setContactModalOpen(false)
        setFeedbackModalOpen(false)
      }
    }

    document.body.style.overflow = "hidden"
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [contactModalOpen, feedbackModalOpen])

  return (
    <div className={`min-h-screen bg-slate-950 text-white ${template.pageClass}`} style={pageStyle}>
      <Head title={pageTitle}>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={card.public_url} />
        <meta property="og:site_name" content="Cardly" />
        <meta property="og:locale" content="he_IL" />
        {socialImage ? <meta property="og:image" content={socialImage} /> : null}
        {socialImage ? <meta property="og:image:alt" content={card.title} /> : null}
        <meta name="twitter:card" content={socialImage ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {socialImage ? <meta name="twitter:image" content={socialImage} /> : null}
        <link rel="canonical" href={card.public_url} />
        {preview ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow" />}
        {!preview ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: structuredData }} /> : null}
      </Head>
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col">
        <div className="relative overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ background: template.heroGradient(r, g, b) }}
          />
            <div className="absolute inset-0 opacity-35">
              {card.cover_image_url ? (
              <img src={card.cover_image_url} alt="" className="h-full w-full object-cover" style={{ objectPosition: coverPosition }} />
              ) : null}
            </div>
          <div className="relative px-4 pb-10 pt-4 sm:px-6">
            <div className="flex items-center justify-end">
              {preview ? <Badge tone="amber">תצוגה מקדימה</Badge> : null}
            </div>
            {sectionVisible("hero") ? (
              <div className="card-hero-content mt-24 flex flex-col items-center text-center">
                {sectionTitleVisible("hero") ? <div className="mb-4 inline-flex rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-white/90">{sectionTitle("hero")}</div> : null}
                <div className="mb-4 h-24 w-24 overflow-hidden border border-white/20 bg-white/10 shadow-2xl" style={{ borderRadius: card.logo_radius === 999 ? "50%" : `${card.logo_radius ?? 24}px` }}>
                  {card.logo_url ? <img src={card.logo_url} alt={card.title} className="h-full w-full object-cover" style={{ objectPosition: logoPosition, borderRadius: card.logo_radius === 999 ? "50%" : `${card.logo_radius ?? 24}px` }} /> : null}
                </div>
                <h1 className="text-3xl font-bold">{card.title}</h1>
                {card.subtitle ? <p className="mt-2 text-lg text-white/85">{card.subtitle}</p> : null}
                {sectionVisible("social_links") && card.social_links.length > 0 ? (
                  <div className="mt-5 flex flex-wrap justify-center gap-3">
                    {card.social_links.map((link) => (
                      <a
                        key={link.id}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex flex-col items-center gap-2 rounded-2xl px-3 py-2 text-center text-white"
                      >
                        <SocialIconBadge
                          platform={link.platform}
                          backgroundColor={normalizeHexColor(link.icon_color || getSocialPlatformColor(link.platform))}
                        />
                        <span className="text-[11px] font-medium text-white/90">
                          {getSocialPlatformLabel(link.platform)}
                        </span>
                      </a>
                    ))}
                  </div>
                ) : null}
                {card.description ? <p className="mt-4 max-w-xl text-sm leading-7 text-white/82">{card.description}</p> : null}
                <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:justify-center">
                  {card.show_vcf_button ? (
                    <ActionButton
                      href={card.vcf_url}
                      label="שמור אותי באנשי קשר"
                      tone="soft"
                      download
                      style={{
                        backgroundColor: "rgba(255,255,255,0.92)",
                        color: "var(--card-primary)",
                      }}
                    />
                  ) : null}
                  {card.show_contact_button ? (
                    <button
                      type="button"
                      onClick={() => setContactModalOpen(true)}
                      className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-medium text-white backdrop-blur transition hover:bg-white/15"
                    >
                      צור קשר
                    </button>
                  ) : null}
                  {card.show_feedback_button ? (
                    <button
                      type="button"
                      onClick={() => setFeedbackModalOpen(true)}
                      className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-medium text-white backdrop-blur transition hover:bg-white/15"
                    >
                      השאירו המלצה
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="card-content flex-1 rounded-t-[2rem] bg-slate-50 px-4 pb-10 pt-6 text-slate-900 shadow-[0_-20px_50px_rgba(15,23,42,0.25)] sm:px-6">
          {sectionOrder.map((section) => {
            if (!sectionVisible(section)) {
              return null
            }

            if (section === "hero") {
              return null
            }

            if (section === "info") {
              return null
            }

            if (section === "buttons" && card.buttons.length > 0) {
              return (
                <section key={section} className="card-section mt-8 p-5">
                  {sectionTitleVisible(section) ? <h2 className="section-title">{sectionTitle(section)}</h2> : null}
                  <div className="mt-3 grid gap-3">
                    {card.buttons.map((button) => {
                      const buttonColor = normalizeHexColor(button.button_color || card.primary_color)
                      const { r: buttonR, g: buttonG, b: buttonB } = hexToRgb(buttonColor)
                      const buttonText = contrastingTextColor(buttonColor)

                      return (
                        <ActionButton
                          key={button.id}
                          href={button.url}
                          label={button.label}
                          tone="primary"
                          style={{
                            backgroundColor: buttonColor,
                            color: buttonText,
                            boxShadow: `0 16px 36px rgba(${buttonR} ${buttonG} ${buttonB} / 0.24)`,
                          }}
                          icon={<IconBadge name={button.icon_name} className="bg-white/20 text-current" />}
                          onClick={() => trackAction("custom")}
                        />
                      )
                    })}
                  </div>
                </section>
              )
            }

            if (section === "social_links" && card.social_links.length > 0) {
              return null
            }

            if (section === "opening_hours" && !card.opening_hours_hidden && card.opening_hours.length > 0) {
              return (
                <section key={section} className="card-section mt-8 p-5">
                  {sectionTitleVisible(section) ? <h2 className="section-title">{sectionTitle(section)}</h2> : null}
                  <div className="mt-3 space-y-2">
                    {card.opening_hours.map((hour) => (
                      <div key={hour.day_of_week} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3 shadow-sm">
                        <span className="font-medium">{dayNames[hour.day_of_week]}</span>
                        <span className="text-sm text-slate-600">
                          {hour.is_closed ? "סגור" : `${hour.opens_at ?? "--:--"} - ${hour.closes_at ?? "--:--"}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )
            }

            if (section === "faq" && card.faqs.length > 0) {
              return (
                <section key={section} className="card-section mt-8 p-5">
                  {sectionTitleVisible(section) ? <h2 className="section-title">{sectionTitle(section)}</h2> : null}
                  <div className="mt-4 space-y-3">
                    {card.faqs.map((faq, index) => {
                      const open = faqOpenIndex === index
                      return (
                        <div key={faq.id} className="overflow-hidden rounded-2xl border border-slate-200">
                          <button
                            type="button"
                            onClick={() => setFaqOpenIndex(open ? null : index)}
                            className="flex w-full items-center justify-between gap-4 bg-slate-50 px-4 py-4 text-right"
                          >
                            <span className="font-medium text-slate-900">{faq.question}</span>
                            <span className="text-xl leading-none text-slate-400">{open ? "−" : "+"}</span>
                          </button>
                          {open ? (
                            <div className="px-4 py-4 leading-7 text-slate-600">
                              {faq.answer}
                            </div>
                          ) : null}
                        </div>
                      )
                    })}
                  </div>
                </section>
              )
            }

            if (section === "testimonials" && card.testimonials.length > 0) {
              const currentIndex = testimonialIndex % card.testimonials.length
              const testimonial = card.testimonials[currentIndex]

              return (
                <section key={section} className="card-section mt-8 p-5">
                  <div className="flex items-center justify-between gap-3">
                    {sectionTitleVisible(section) ? <h2 className="section-title">{sectionTitle(section)}</h2> : <span />}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="btn-secondary px-3 py-2 text-xs"
                        onClick={() => setTestimonialIndex((index) => (index - 1 + card.testimonials.length) % card.testimonials.length)}
                        disabled={card.testimonials.length <= 1}
                      >
                        קודם
                      </button>
                      <button
                        type="button"
                        className="btn-secondary px-3 py-2 text-xs"
                        onClick={() => setTestimonialIndex((index) => (index + 1) % card.testimonials.length)}
                        disabled={card.testimonials.length <= 1}
                      >
                        הבא
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 rounded-[1.75rem] bg-slate-50 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-lg font-semibold text-slate-900">{testimonial.name}</div>
                        {testimonial.role ? <div className="mt-1 text-sm text-slate-500">{testimonial.role}</div> : null}
                      </div>
                      <div className="rounded-full px-3 py-1 text-sm font-semibold" style={{ backgroundColor: primary, color: primaryText }}>
                        {testimonial.rating}.0
                      </div>
                    </div>
                    <p className="mt-4 leading-8 text-slate-700">{testimonial.message}</p>
                  </div>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="flex gap-1">
                      {card.testimonials.map((_, index) => (
                        <span
                          key={index}
                          className={`h-2.5 w-2.5 rounded-full ${index === currentIndex ? "" : "bg-slate-300"}`}
                          style={index === currentIndex ? { backgroundColor: primary } : undefined}
                        />
                      ))}
                    </div>
                    {card.show_feedback_button ? (
                      <button
                        type="button"
                        onClick={() => setFeedbackModalOpen(true)}
                        className="text-sm font-medium text-[var(--card-primary)]"
                      >
                        השאירו המלצה
                      </button>
                    ) : null}
                  </div>
                </section>
              )
            }

            if (section === "gallery" && card.gallery_images.length > 0) {
              return (
                <section key={section} className="card-section mt-8 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    {sectionTitleVisible(section) ? <h2 className="section-title">{sectionTitle(section)}</h2> : <span />}
                    {galleryCategories.length > 1 ? (
                      <div className="flex flex-wrap gap-2" aria-label="קטגוריות בגלריה">
                        <button
                          type="button"
                          onClick={() => setGalleryCategory("all")}
                          className={`rounded-full px-3 py-1.5 text-xs font-medium ${galleryCategory === "all" ? "text-white" : "bg-white text-slate-600"}`}
                          style={galleryCategory === "all" ? { backgroundColor: primary } : undefined}
                        >
                          הכל
                        </button>
                        {galleryCategories.map((category) => (
                          <button
                            key={category}
                            type="button"
                            onClick={() => setGalleryCategory(category)}
                            className={`rounded-full px-3 py-1.5 text-xs font-medium ${galleryCategory === category ? "text-white" : "bg-white text-slate-600"}`}
                            style={galleryCategory === category ? { backgroundColor: primary } : undefined}
                          >
                            {category}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    {visibleGalleryImages.map((image, index) => (
                      <button
                        key={image.id}
                        type="button"
                        onClick={() => setLightboxIndex(index)}
                        className="group overflow-hidden rounded-3xl text-left shadow-sm"
                        aria-label={`פתח תמונה ${index + 1}`}
                      >
                        <img
                          src={image.url}
                          alt={image.description || `${card.title} - תמונה ${index + 1}`}
                          className="h-40 w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                        />
                      </button>
                    ))}
                  </div>
                </section>
              )
            }

            return null
          })}

          <section className="mt-8 rounded-3xl bg-white p-5 shadow-sm">
            <h2 className="section-title">שיתוף הכרטיס</h2>
            <p className="mt-1 text-sm text-slate-500">אפשר לסרוק או להעתיק את הקישור</p>
            <div className="mt-4 grid gap-4 md:grid-cols-[180px_1fr] md:items-center">
              <div className="mx-auto rounded-3xl p-3" style={{ backgroundColor: `rgba(${r} ${g} ${b} / 0.06)` }}>
                <QRCode value={card.public_url} size={164} bgColor="#f8fafc" fgColor={primary} />
              </div>
              <div className="space-y-3">
                <div
                  className="rounded-2xl border px-4 py-3 text-sm break-all"
                  dir="ltr"
                  style={{
                    borderColor: `rgba(${r} ${g} ${b} / 0.18)`,
                    backgroundColor: `rgba(${r} ${g} ${b} / 0.06)`,
                    color: "var(--card-primary)",
                  }}
                >
                  {card.public_url}
                </div>
                <div className="flex flex-wrap gap-2">
                  {card.show_vcf_button ? (
                    <ActionButton
                      href={card.vcf_url}
                      label="שמור אותי באנשי קשר"
                      tone="primary"
                      download
                      onClick={() => trackAction("vcf")}
                      style={{
                        backgroundColor: primary,
                        color: primaryText,
                      }}
                    />
                  ) : null}
                  <a
                    className="btn-secondary"
                    href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      borderColor: `rgba(${r} ${g} ${b} / 0.18)`,
                      color: "var(--card-primary)",
                    }}
                  >
                    שיתוף בוואטסאפ
                  </a>
                  <a
                    className="btn-secondary"
                    href={`mailto:?subject=${encodeURIComponent(card.title)}&body=${encodeURIComponent(card.public_url)}`}
                    style={{
                      borderColor: `rgba(${r} ${g} ${b} / 0.18)`,
                      color: "var(--card-primary)",
                    }}
                  >
                    שליחה באימייל
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {lightboxIndex !== null ? (
        <GalleryLightbox
          images={visibleGalleryImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      ) : null}

      {contactModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-4 backdrop-blur-sm sm:items-center" onClick={() => setContactModalOpen(false)}>
          <div
            className="w-full max-w-lg overflow-hidden rounded-[2rem] border border-white/10 bg-white text-slate-900 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="flex items-start justify-between gap-4 px-5 py-4 text-white"
              style={{
                background: `linear-gradient(135deg, ${primary} 0%, rgba(${r} ${g} ${b} / 0.82) 100%)`,
              }}
            >
              <div>
                <h3 className="text-2xl font-bold">יצירת קשר</h3>
                <p className="mt-1 text-sm text-white/80">השאר/י שם, טלפון והודעה ונחזור אליך</p>
              </div>
              <button type="button" className="rounded-2xl bg-white/15 px-3 py-2 text-xs font-medium text-white backdrop-blur" onClick={() => setContactModalOpen(false)}>
                סגור
              </button>
            </div>
            <form
              className="space-y-4 p-5"
              onSubmit={(event) => {
                event.preventDefault()
                postContact(`/${card.slug}/contact`, {
                  preserveScroll: true,
                  onSuccess: () => {
                    resetContact()
                    setContactModalOpen(false)
                  },
                })
              }}
            >
                <div className="grid gap-4">
                  <label className="block">
                    <span className="label-base">שם מלא</span>
                    <TextInput required value={contactData.name} onChange={(e) => setContactData("name", e.target.value)} />
                  {contactErrors.name ? <span className="mt-2 block text-sm text-rose-600">{contactErrors.name}</span> : null}
                </label>
                <label className="block">
                  <span className="label-base">טלפון</span>
                  <TextInput required dir="ltr" value={contactData.phone} onChange={(e) => setContactData("phone", e.target.value)} />
                  {contactErrors.phone ? <span className="mt-2 block text-sm text-rose-600">{contactErrors.phone}</span> : null}
                </label>
                <label className="block">
                  <span className="label-base">הודעה</span>
                  <textarea
                    required
                    className="input-base min-h-32"
                    value={contactData.message}
                    onChange={(e) => setContactData("message", e.target.value)}
                  />
                  {contactErrors.message ? <span className="mt-2 block text-sm text-rose-600">{contactErrors.message}</span> : null}
                </label>
              </div>
              <button
                disabled={contactProcessing}
                className="inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-sm font-medium text-white shadow-soft transition disabled:opacity-60"
                style={{ backgroundColor: primary, color: primaryText }}
              >
                שליחה
              </button>
            </form>
          </div>
        </div>
      ) : null}

      {feedbackModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-4 backdrop-blur-sm sm:items-center" onClick={() => setFeedbackModalOpen(false)}>
          <div
            className="w-full max-w-lg overflow-hidden rounded-[2rem] border border-white/10 bg-white text-slate-900 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className="flex items-start justify-between gap-4 px-5 py-4 text-white"
              style={{
                background: `linear-gradient(135deg, ${primary} 0%, rgba(${r} ${g} ${b} / 0.82) 100%)`,
              }}
            >
              <div>
                <h3 className="text-2xl font-bold">השארת המלצה</h3>
                <p className="mt-1 text-sm text-white/80">ההמלצה תישמר לבדיקה ותופיע רק לאחר אישור</p>
              </div>
              <button type="button" className="rounded-2xl bg-white/15 px-3 py-2 text-xs font-medium text-white backdrop-blur" onClick={() => setFeedbackModalOpen(false)}>
                סגור
              </button>
            </div>
            <form
              className="space-y-4 p-5"
              onSubmit={(event) => {
                event.preventDefault()
                postFeedback(`/${card.slug}/feedback`, {
                  preserveScroll: true,
                  onSuccess: () => {
                    resetFeedback()
                    setFeedbackModalOpen(false)
                  },
                })
              }}
            >
              <div className="grid gap-4">
                <label className="block">
                  <span className="label-base">שם מלא</span>
                  <TextInput required value={feedbackData.name} onChange={(e) => setFeedbackData("name", e.target.value)} />
                  {feedbackErrors.name ? <span className="mt-2 block text-sm text-rose-600">{feedbackErrors.name}</span> : null}
                </label>
                <label className="block">
                  <span className="label-base">תפקיד / עסק</span>
                  <TextInput value={feedbackData.role} onChange={(e) => setFeedbackData("role", e.target.value)} />
                  {feedbackErrors.role ? <span className="mt-2 block text-sm text-rose-600">{feedbackErrors.role}</span> : null}
                </label>
                <label className="block">
                  <span className="label-base">דירוג</span>
                  <select className="input-base" value={feedbackData.rating} onChange={(e) => setFeedbackData("rating", e.target.value)}>
                    {Array.from({ length: 5 }, (_, index) => String(index + 1)).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                  {feedbackErrors.rating ? <span className="mt-2 block text-sm text-rose-600">{feedbackErrors.rating}</span> : null}
                </label>
                <label className="block">
                  <span className="label-base">המלצה</span>
                  <textarea
                    required
                    className="input-base min-h-32"
                    value={feedbackData.message}
                    onChange={(e) => setFeedbackData("message", e.target.value)}
                  />
                  {feedbackErrors.message ? <span className="mt-2 block text-sm text-rose-600">{feedbackErrors.message}</span> : null}
                </label>
              </div>
              <button
                disabled={feedbackProcessing}
                className="inline-flex w-full items-center justify-center rounded-2xl px-4 py-3 text-sm font-medium text-white shadow-soft transition disabled:opacity-60"
                style={{ backgroundColor: primary, color: primaryText }}
              >
                שליחת המלצה
              </button>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  )
}
